"use client";
import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { formatCompactNumber, formatIndianNumber } from "@/utils/commonFunctions";

type SWPMonthlyData = {
  tenure: number;
  balance: string;
  currValue: number;
  totalInterestEarned: string;
  totalWithdrawn: string;
  interest: string;
  withdrawalAmount: string;
};

const THEME: {
  line: string;
  axisLine: string;
  labelColor: string;
  guideColor: string;
  showYAxisLabels: boolean;
} = {
  line: "#1A51AA",
  axisLine: "#D9E0EC",
  labelColor: "#00000080",
  guideColor: "#94A3B8",
  showYAxisLabels: true,
};

function drawGuideLine(chart: Highcharts.Chart, x: number, y: number) {
  const target = chart as Highcharts.Chart & { hoverLine?: Highcharts.SVGElement };

  const yBottom = chart.yAxis[0].toPixels(0, false) as number;
  const path: Highcharts.SVGPathArray = [
    ["M", x, yBottom],
    ["L", x, y],
  ];

  if (target.hoverLine) {
    target.hoverLine.attr({ d: path });
    return;
  }

  target.hoverLine = chart.renderer
    .path(path)
    .attr({
      stroke: THEME.guideColor,
      "stroke-width": 1,
      dashstyle: "Dash",
      zIndex: 1,
    })
    .add();
}

export default function RetirementChart({ data }: { data: SWPMonthlyData[] }) {
  const result = useMemo(() => {
    if (!data.length) {
      return {
        seriesData: [] as Highcharts.PointOptionsObject[],
        tickPositions: [] as number[],
        maxTenure: 0,
      };
    }

    const maxTenure = Math.max(...data.map((d) => d.tenure));

    const seriesData: Highcharts.PointOptionsObject[] = data.map((d) => ({
      x: d.tenure,
      y: Number(d.balance.replace(/,/g, "")),
      custom: d,
    }));

    const desiredTicks = 7;
    const step = Math.max(1, Math.round(maxTenure / desiredTicks));

    const tickPositions: number[] = [];
    for (let i = 1; i <= maxTenure; i += step) {
      tickPositions.push(i);
    }
    if (!tickPositions.includes(maxTenure)) {
      tickPositions.push(maxTenure);
    }

    return { seriesData, tickPositions, maxTenure };
  }, [data]);

  const option: Highcharts.Options = {
    chart: {
      type: "line",
      backgroundColor: "transparent",
      height: 160,
    },
    title: undefined,
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      min: 1,
      max: result.maxTenure || undefined,
      tickPositions: result.tickPositions,
      tickLength: 0,
      lineColor: THEME.axisLine,
      labels: {
        style: { color: THEME.labelColor, fontWeight: "500", fontSize: "12px" },
        formatter() {
          return String(Math.max(1, Math.round((this.value as number) / 12)));
        },
      },
    },
    yAxis: {
      title: undefined,
      gridLineWidth: 0,
      min: 0,
      tickAmount: 3,
      labels: {
        enabled: THEME.showYAxisLabels,
        style: { color: THEME.labelColor, fontWeight: "500", fontSize: "12px" },
        formatter() {
          return `₹${formatCompactNumber(this.value as number, true, 2)}`;
        },
      },
    },
    tooltip: {
      enabled: true,
      formatter: function (this: Highcharts.Point) {
        const d = (this as Highcharts.Point & { custom: SWPMonthlyData }).custom;

        return `
        <b>Month</b> ${d.tenure}<br/><b>Balance:</b> ${formatIndianNumber(d.balance, { prefix: "₹" })}<br/>
      `;
      },
    },
    plotOptions: {
      series: {
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 5,
              fillColor: THEME.line,
              lineWidth: 2,
              lineColor: "#FFFFFF",
            },
          },
        },
        states: {
          hover: { halo: { size: 0 } },
        },

        point: {
          events: {
            mouseOver: function (this: Highcharts.Point) {
              if (this.x === undefined || this.y === undefined) return;

              const chart = this.series.chart;
              const x = chart.xAxis[0].toPixels(this.x, false) as number;
              const y = chart.yAxis[0].toPixels(this.y, false) as number;

              drawGuideLine(chart, x, y);
            },

            mouseOut: function (this: Highcharts.Point) {
              const chart = this.series.chart as Highcharts.Chart & {
                hoverLine?: Highcharts.SVGElement;
              };

              if (chart.hoverLine) {
                chart.hoverLine.destroy();
                chart.hoverLine = undefined;
              }
            },
          },
        },
      },
    },
    series: [
      {
        type: "line",
        data: result.seriesData,
        lineWidth: 2,
        color: THEME.line,
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={option} />;
}
